import { useLayoutEffect } from 'react';

// Opt-in only. Read current geometry at each tick, so resize and manual scrolling stay in sync.
export default function useShelfAutoAdvance(ref, delay, itemKey, count, loop = false) {
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || !delay || count < 2) return;
    let previousStep = 0;
    const align = () => {
      const step =
        (node.children[0]?.getBoundingClientRect().width || 0) +
        (parseFloat(getComputedStyle(node).columnGap) || 0);
      if (!loop || step <= 0) return;
      const index = previousStep
        ? ((Math.round(node.scrollLeft / previousStep) % count) + count) % count
        : 0;
      node.scrollTo?.({ left: (count + index) * step, behavior: 'instant' });
      previousStep = step;
    };
    align();
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(align) : null;
    resizeObserver?.observe(node);
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let visible = false;
    let hovered = false;
    let dragging = false;
    let focused = false;
    let timer;
    let settleTimer;
    const cancel = () => clearTimeout(timer);
    const schedule = () => {
      cancel();
      if (!visible || document.hidden || media?.matches || hovered || dragging || focused) return;
      timer = setTimeout(() => {
        const max = node.scrollWidth - node.clientWidth;
        const width = node.children[0]?.getBoundingClientRect().width || 0;
        const gap = parseFloat(getComputedStyle(node).columnGap) || 0;
        // All cards already visible, a single card, or a temporarily unmeasurable shelf needs no motion.
        if (max > 2 && width > 0) {
          const step = width + gap;
          const end = node.scrollLeft >= max - 2;
          node.scrollTo({
            left: loop
              ? (Math.round(node.scrollLeft / step) + 1) * step
              : end
                ? 0
                : Math.min(max, node.scrollLeft + step),
            behavior: !loop && end ? 'auto' : 'smooth',
          });
        }
        schedule();
      }, delay);
    };
    const intersection =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (entries) => {
              visible = entries.some(
                (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5,
              );
              schedule();
            },
            { threshold: [0, 0.5] },
          )
        : null;
    // Without visibility observation, keep this optional animation stopped rather than running off screen.
    intersection?.observe(node);
    const enter = (event) => {
      if (event.pointerType !== 'touch') {
        hovered = true;
        schedule();
      }
    };
    const leave = () => {
      hovered = false;
      schedule();
    };
    const down = () => {
      dragging = true;
      schedule();
    };
    const up = () => {
      if (dragging) {
        dragging = false;
        schedule();
      }
    };
    const focus = () => {
      focused = true;
      schedule();
    };
    const blur = (event) => {
      if (!node.contains(event.relatedTarget)) {
        focused = false;
        schedule();
      }
    };
    // Ghost cycles on both sides keep previous/next cards visible at the first and last item.
    // Normalize only after scroll settles, not while a smooth scroll/drag is in progress.
    const scroll = () => {
      schedule();
      clearTimeout(settleTimer);
      if (!loop) return;
      settleTimer = setTimeout(() => {
        const width = node.children[0]?.getBoundingClientRect().width || 0;
        const step = width + (parseFloat(getComputedStyle(node).columnGap) || 0);
        const cycle = step * count;
        if (
          !dragging &&
          cycle > 0 &&
          (node.scrollLeft < cycle - 1 || node.scrollLeft >= cycle * 2 - 1)
        ) {
          node.scrollTo({
            left: cycle + (((node.scrollLeft % cycle) + cycle) % cycle),
            behavior: 'instant',
          });
        }
      }, 150);
    };
    const bindings = [
      ['scroll', scroll],
      ['pointerenter', enter],
      ['pointerleave', leave],
      ['pointerdown', down],
      ['focusin', focus],
      ['focusout', blur],
    ];
    bindings.forEach(([type, listener]) =>
      node.addEventListener(type, listener, { passive: true }),
    );
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    window.addEventListener('resize', schedule);
    document.addEventListener('visibilitychange', schedule);
    media?.addEventListener?.('change', schedule);
    return () => {
      cancel();
      resizeObserver?.disconnect();
      clearTimeout(settleTimer);
      intersection?.disconnect();
      bindings.forEach(([type, listener]) => node.removeEventListener(type, listener));
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', schedule);
      media?.removeEventListener?.('change', schedule);
    };
  }, [ref, delay, itemKey, count, loop]);
}
